import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { finalize } from 'rxjs';
import { CHANNEL_ORDER } from '../../../programplan/channel-order';
import { DisableUnlessPermissionDirective } from '../../../../../security/disable-unless-permission.directive';
import {
  ChannelEngine,
  ChannelEngineRollout,
  ChannelSettingsService,
  LiveEngine,
} from '../../services/channel-settings.service';

interface ChannelRow {
  channel: string;
  engine: LiveEngine;
  isExplicit: boolean;
  saving: boolean;
}

/**
 * 2026-09-07 — welche Engine einen Sender live transkribiert.
 *
 * Bis jetzt war Whisper der stille Standard: BroadcastScheduleService startet
 * für jede KIUT-Sendung eine transcribe-live-Instanz, und Voxtral musste pro
 * Sendung von Hand eingeplant werden. Genau daran ist ORFS regelmäßig hängen
 * geblieben. Steht ein Sender hier auf Voxtral, wird jede ab dann als KIUT
 * markierte Sendung automatisch für Voxtral eingeplant.
 *
 * 2026-09-09 — die Umstellung greift zusätzlich auf die Sendungen, die schon
 * im Plan stehen, aber noch nicht begonnen haben (heute Rest + morgen). Sonst
 * bliebe der ganze restliche Sendetag auf der alten Engine. Laufende Sessions
 * und ausdrückliche Operator-Entscheidungen bleiben unangetastet.
 */
@Component({
  selector: 'app-channel-engine-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, DisableUnlessPermissionDirective],
  templateUrl: './channel-engine-panel.component.html',
  styleUrl: './channel-engine-panel.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChannelEnginePanelComponent {

  /** Der Tab lädt erst, wenn er zum ersten Mal sichtbar wird. */
  @Input() set active(value: boolean) {
    if (value && !this.loaded && !this.loading) {
      this.load();
    }
  }

  rows: ChannelRow[] = [];
  loading = false;
  loaded = false;
  error: string | null = null;

  /** Result of the most recent channel switch, shown until the next change. */
  lastRollout: ChannelEngineRollout | null = null;

  constructor(private channelSettings: ChannelSettingsService) {}

  load(): void {
    this.loading = true;
    this.error = null;

    this.channelSettings.list()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: settings => {
          const byChannel = new Map(settings.map(s => [s.channel, s]));

          // Reihenfolge aus CHANNEL_ORDER, damit alle Sender in fester
          // Reihenfolge erscheinen — auch die, für die noch nie ein Wert
          // geschrieben wurde. Sender, die das Backend zusätzlich kennt,
          // werden hinten angehängt statt verschluckt.
          const ordered = [
            ...CHANNEL_ORDER,
            ...settings.map(s => s.channel).filter(c => !CHANNEL_ORDER.includes(c)),
          ];

          this.rows = ordered.map(channel => {
            const s: ChannelEngine | undefined = byChannel.get(channel);
            return {
              channel,
              engine: s?.primaryEngine ?? 'whisper',
              isExplicit: s?.isExplicit ?? false,
              saving: false,
            };
          });
          this.loaded = true;
        },
        error: err => this.error = this.messageOf(err),
      });
  }

  onEngineChange(row: ChannelRow, engine: LiveEngine): void {
    const previous = { engine: row.engine, isExplicit: row.isExplicit };
    row.engine = engine;
    row.saving = true;
    this.error = null;
    this.lastRollout = null;

    this.channelSettings.setPrimaryEngine(row.channel, engine)
      .pipe(finalize(() => row.saving = false))
      .subscribe({
        next: saved => {
          row.engine = saved.primaryEngine;
          row.isExplicit = saved.isExplicit;
          this.lastRollout = saved.rollout ?? null;
        },
        error: err => {
          // Zurückrollen, sonst zeigt die Liste einen Wert, der nie gespeichert wurde.
          row.engine = previous.engine;
          row.isExplicit = previous.isExplicit;
          this.error = this.messageOf(err);
        },
      });
  }

  iconFor(channel: string): string {
    const svg = ['ORF1', 'ORF2', 'ORF3', 'KIDS', 'ORFS'].includes(channel);
    // ORF-NOE -> NOE.png, ORF1 -> ORF1.svg
    const file = channel.includes('-') ? channel.split('-')[1] : channel;
    return `assets/${file}.${svg ? 'svg' : 'png'}`;
  }

  get voxtralCount(): number {
    return this.rows.filter(r => r.engine === 'voxtral').length;
  }

  private messageOf(err: unknown): string {
    const e = err as { error?: unknown; message?: string };
    if (typeof e?.error === 'string' && e.error) return e.error;
    return e?.message ?? 'Unbekannter Fehler';
  }
}
